// ======================================================================
// Enhanced FollowupList with Dashboard Drilldown Support
// ======================================================================

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import fmsService from "../../services/fmsService";

import FollowupDrawer from "./FollowupDrawer";
import FollowupKpiBar from "./FollowupKpiBar";
import FollowupFilters from "./FollowupFilters";

import { applyFilters } from "./followupUtils";

export default function FollowupList() {

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
    domain: "",
  });

  // -----------------------------------------------------------
  // Load Data
  // -----------------------------------------------------------
  const loadData = async () => {
  try {

    setLoading(true);

    const res = await fmsService.getFollowupList(
      page,
      pageSize,
      filters.status,
      filters.search
    );

    const data = res.data?.items || [];

    console.log(
  "Status Filter:",
  filters.status,
  "Records Returned:",
  data.length,
  data
);

    setItems(data);
    setTotal(res.data?.total || 0);

    applyQueryFilters(data);

  } catch (err) {

    console.error(err);
    alert("Failed to load followups");

  } finally {

    setLoading(false);

  }
};

 useEffect(() => {
  loadData();
}, [
  page,
  filters.status
]);

  // -----------------------------------------------------------
  // Apply Query Params (Dashboard Drilldown)
  // -----------------------------------------------------------
  const applyQueryFilters = (data) => {

    const status = searchParams.get("status");
    const domain = searchParams.get("domain");
    const date = searchParams.get("date");
    const overdue = searchParams.get("overdue");

    let updatedFilters = { ...filters };

    if (status) updatedFilters.status = status;
    if (domain) updatedFilters.domain = domain;
    if (date) {
      updatedFilters.fromDate = date;
      updatedFilters.toDate = date;
    }

    setFilters(updatedFilters);

    let result = data;
      if (!status && !filters.status) {

          result = result.filter(
            r =>
              r.status_code === "OPEN" ||
              r.status_code === "IN_PROGRESS"
          );

        }

    if (status) {
      result = result.filter(r => r.status_code === status);
    }

    if (domain) {
      result = result.filter(r => r.domain_code === domain);
    }

    if (overdue === "true") {
      result = result.filter(
        r =>
          r.status_code !== "COMPLETED" &&
          new Date(r.due_date) < new Date()
      );
    }

    if (date) {
      result = result.filter(
        r => r.created_at?.slice(0, 10) === date
      );
    }

    setFiltered(result);
  };

  // -----------------------------------------------------------
  // Manual Filter Handlers
  // -----------------------------------------------------------
  const handleSearch = async () => {

  if (page !== 1) {
    setPage(1);
    return;
  }

  await loadData();

};

  const handleReset = () => {
    setFilters({
      search: "",
      status: "",
      fromDate: "",
      toDate: "",
      domain: "",
    });

    setFiltered(
        items.filter(
          r =>
            r.status_code === "OPEN" ||
            r.status_code === "IN_PROGRESS"
        )
      );
  };

  
  const totalPages = Math.ceil(
  total / pageSize
);

  // -----------------------------------------------------------
  // Render
  // -----------------------------------------------------------
  return (
    <div>

      {/* HEADER */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #059669 0%, #10b981 50%, #34d399 100%)",
          borderRadius: 12,
          padding: "18px 22px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 8px 20px rgba(0,0,0,0.15)"
        }}
      >
        <div>
          <h4 style={{ margin: 0, fontWeight: 600 }}>
            Followup Management
          </h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Centralized tracking of tasks and reminders
          </div>
        </div>

        <div style={{ display: "flex", gap: 12 }}>

          <button
            onClick={() => navigate("/ppe/fms/dashboard")}
            style={{
              background: "linear-gradient(135deg, #4f46e5, #9333ea)",
              color: "#fff",
              border: "none",
              padding: "8px 18px",
              borderRadius: 8,
              fontWeight: 500,
              cursor: "pointer"
            }}
          >
            📊 Dashboard
          </button>

          <button
            className="btn btn-light"
            onClick={() => setDrawerOpen(true)}
            style={{
              padding: "8px 18px",
              fontWeight: 500,
              borderRadius: 8
            }}
          >
            + Create Followup
          </button>

        </div>
      </div>

      <FollowupKpiBar items={items} />

      <FollowupFilters
        filters={filters}
        setFilters={setFilters}
        onSearch={handleSearch}
        onReset={handleReset}
      />

      {/* TABLE */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>No</th>
                <th>Title</th>
                <th>Domain</th>
                <th>Purpose</th>
                <th>Priority</th>
                <th>Reference No</th>
                <th>Assigned To</th>
                <th>Status</th>
                <th>Due Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>

              {loading && (
                <tr>
                  <td colSpan="9" className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center text-muted">
                    No followups found
                  </td>
                </tr>
              )}

              {!loading &&
                filtered.map((row) => (
                  <tr key={row.id}>
                    <td>{row.followup_no}</td>
                    <td>{row.title}</td>
                    <td>{row.domain_code || "-"}</td>
                    <td>{row.purpose_code || "-"}</td>
                    
                    <td>{row.priority}</td>
                    <td>
                      {row.reference_display || "-"}
                    </td>
                    <td>{row.assigned_to_name || row.assigned_to || "-"}</td>
                    <td>{row.status_code}</td>
                    <td>
                      {(() => {

                        const today = new Date();
                        today.setHours(0, 0, 0, 0);

                        const dueDate = row.due_date
                          ? new Date(row.due_date)
                          : null;

                        let color = "";
                        let fontWeight = "normal";

                        if (dueDate) {

                          dueDate.setHours(0, 0, 0, 0);

                          if (dueDate < today) {
                            color = "#dc2626";     // Red
                            fontWeight = "600";
                          }
                          else if (
                            dueDate.getTime() === today.getTime()
                          ) {
                            color = "#ea580c";     // Orange
                            fontWeight = "600";
                          }

                        }

                        return (
                          <span
                            style={{
                              color,
                              fontWeight
                            }}
                          >
                            {row.due_date}
                          </span>
                        );

                      })()}
                    </td>
                    <td>
                      <span
                        style={{
                          cursor: "pointer",
                          color: "#2563eb",
                          textDecoration: "underline",
                        }}
                        onClick={() =>
                          navigate(`/ppe/fms/followups/${row.id}`)
                        }
                      >
                        View
                      </span>
                    </td>
                  </tr>
                ))}

            </tbody>
          </table>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 15
        }}
      >

        <div>
          Total Records: {total}
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center"
          }}
        >

          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </button>

          <span>
            Page {page} of {totalPages}
          </span>

          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>

        </div>

      </div>          
      <FollowupDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSaved={loadData}
      />

    </div>
  );
}
